import {
  ApiTokenListResponse,
  CancellationDatesDTO,
  CheckInPassDTO,
  CurrentSubscriptionDTO,
  DetailedInvoiceDTO,
  InvoiceDTO,
  InvoiceStatus,
  PaymentMethodDTO,
  ProductOrderDetailDTO,
  SessionPackUsage,
  SubscriptionSessionPackDTO,
  SubscriptionSessionPackUsageHistoryDTO,
  UserMemberBean,
  UserMemberProfileBadgesDTO,
  UserMemberProfileDTO,
} from "@gymflow/types";
import {
  infiniteQueryOptions,
  mutationOptions,
  QueryKey,
  queryOptions,
} from "@tanstack/react-query";
import { DateTime } from "luxon";

import { api } from "../../api";
import { queryClient } from "../../QueryClient";
import { tokenPagination } from "../../tokenPagination";
import { memberApiAccess } from "./memberApiAccess";
import { memberApiAppointment } from "./memberApiAppointment";

const keys = {
  all: () => ["memberApiMember"] as QueryKey,
  id: (userId: unknown) => [...keys.all(), "id", userId] as QueryKey,
  idDeprecated: (userId: unknown) =>
    [...keys.id(userId), "deprecated"] as QueryKey,
  invoice: (userId: unknown, invoiceNumber?: unknown) =>
    [...keys.id(userId), "invoice", invoiceNumber] as QueryKey,
  highlightedInvoice: (userId: unknown) =>
    [...keys.id(userId), "highlightedInvoice"] as QueryKey,
  invoices: (userId: unknown) => [...keys.id(userId), "invoices"] as QueryKey,
  paymentMethod: (userId: unknown, paymentMethodId?: unknown) =>
    [...keys.id(userId), "paymentMethod", paymentMethodId] as QueryKey,
  paymentMethods: (userId: unknown) =>
    [...keys.id(userId), "paymentMethods"] as QueryKey,
  sessionPackUsage: (userId: unknown, params?: unknown) =>
    [...keys.id(userId), "sessionPackUsage", params] as QueryKey,
  agenda: (userId: unknown) => [...keys.id(userId), "agenda"] as QueryKey,
  currentMembershipSubscription: (userId: unknown) =>
    [...keys.id(userId), "currentMembershipSubscription"] as QueryKey,
  calculateCancelDates: ({
    userId,
    membershipId,
  }: {
    userId: unknown;
    membershipId: unknown;
  }) => [...keys.id(userId), "calculateCancelDates", membershipId] as QueryKey,
  resumeMembershipSummary: ({
    userId,
    membershipId,
  }: {
    userId: unknown;
    membershipId: unknown;
  }) =>
    [...keys.id(userId), "resumeMembershipSummary", membershipId] as QueryKey,
  checkInStatus: (userId: unknown) =>
    [...keys.id(userId), "checkInStatus"] as QueryKey,
  checkInPass: (memberId: unknown) =>
    [...keys.id(memberId), "checkInPass"] as QueryKey,
  invoiceFile: (userId: unknown, invoiceNumber?: unknown) =>
    [...keys.id(userId), "invoiceFile", invoiceNumber] as QueryKey,
  currentCreditPack: (userId: unknown) =>
    [...keys.id(userId), "currentCreditPack"] as QueryKey,
  previousCreditPack: (userId: unknown) =>
    [...keys.id(userId), "previousCreditPack"] as QueryKey,
  usageHistoryCreditPack: (params: {
    userId: unknown;
    subscriptionId: unknown;
  }) =>
    [...keys.id(params.userId), "usageHistoryCreditPack", params] as QueryKey,
  profileBadges: (userId: unknown) =>
    [...keys.id(userId), "profileBadges"] as QueryKey,
};

export const memberApiMember = {
  keys,
  queries: {
    id: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.id(userId),
        queryFn: async () => {
          const result = (
            await api.get<UserMemberProfileDTO>(`/customer/user-member-profile`)
          ).data;
          return {
            ...result,
            userMemberId: userId as string,
          } as UserMemberProfileDTO;
        },
        enabled: !!userId,
      }),
    /**
     * @deprecated - use .id() instead
     */
    idDeprecated: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.idDeprecated(userId),
        queryFn: async () => {
          return (await api.get<UserMemberBean>(`/customer/user/member`)).data;
        },
        enabled: !!userId,
      }),
    invoice: (userId: string | undefined, invoiceNumber: string | undefined) =>
      queryOptions({
        queryKey: keys.invoice(userId, invoiceNumber),
        queryFn: async () => {
          return (
            await api.get<DetailedInvoiceDTO>(
              `/customer/invoice/${invoiceNumber}`,
            )
          ).data;
        },
        enabled: !!userId && !!invoiceNumber,
      }),
    invoiceFile: (
      userId: string | undefined,
      invoiceNumber: string | undefined,
    ) =>
      queryOptions({
        queryKey: keys.invoiceFile(userId, invoiceNumber),
        queryFn: async () => {
          return (
            await api.get<Blob>(`customer/invoice/${invoiceNumber}/pdf`, {
              responseType: "blob",
            })
          ).data;
        },
      }),
    highlightedInvoice: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.highlightedInvoice(userId),
        queryFn: async () => {
          return (
            await api.get<DetailedInvoiceDTO>(
              `/customer/user-member-invoice-list/highlighted`,
            )
          ).data;
        },
        enabled: !!userId,
      }),
    paymentMethod: (userId: string | undefined, paymentMethodId: string) =>
      queryOptions({
        queryKey: keys.paymentMethod(userId, paymentMethodId),
        queryFn: async () => {
          return (
            await api.get<PaymentMethodDTO>(
              `/customer/payment-method/${paymentMethodId}`,
            )
          ).data;
        },
        enabled: !!userId,
      }),
    paymentMethods: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.paymentMethods(userId),
        queryFn: async () => {
          return (await api.get<PaymentMethodDTO[]>(`/customer/payment-method`))
            .data;
        },
        enabled: !!userId,
      }),
    sessionPackUsage: (
      userId: string | undefined,
      params:
        | ({ sessionCost: number } & (
            | { appointableCategoryId: number }
            | { activityCategoryId: number }
          ))
        | undefined,
    ) =>
      queryOptions({
        queryKey: keys.sessionPackUsage(userId, params),
        queryFn: async () => {
          return (
            await api.put<SessionPackUsage>(
              `/customer/user/member/session-pack-usage`,
              params,
            )
          ).data;
        },
        enabled: !!userId && !!params,
      }),
    currentMembershipSubscription: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.currentMembershipSubscription(userId),
        queryFn: async () => {
          return (
            await api.get<CurrentSubscriptionDTO>(
              `/customer/current-membership-subscription`,
            )
          ).data;
        },
        enabled: !!userId,
      }),
    calculateCancelDates: ({
      membershipId,
      userId,
    }: {
      membershipId: number;
      userId: string;
    }) =>
      queryOptions({
        queryKey: keys.calculateCancelDates({ membershipId, userId }),
        queryFn: async () => {
          return (
            await api.get<CancellationDatesDTO>(
              `/customer/user/member/membership/calculate-cancel-dates/${membershipId}`,
            )
          ).data;
        },
      }),
    resumeMembershipSummary: (
      userId: string | undefined,
      membershipId: number | undefined,
      resumeDate: string | undefined,
    ) =>
      queryOptions({
        queryKey: keys.resumeMembershipSummary({ userId, membershipId }),
        queryFn: async () => {
          return (
            await api.get<number>(
              `/customer/user/member/calculate-prorata-resume-subscription/${membershipId}`,
              {
                params: {
                  resumeDate,
                },
              },
            )
          ).data;
        },
        enabled: !!userId && !!membershipId && !!resumeDate,
      }),
    checkInStatus: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.checkInStatus(userId),
        queryFn: async () =>
          (
            await api.get<{ isCheckedIn: boolean }>(
              `/customer/access/check-in/status`,
            )
          ).data,
        enabled: !!userId,
      }),
    checkInPass: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.checkInPass(userId),
        queryFn: async () =>
          (await api.get<CheckInPassDTO>("/customer/check-in/pass")).data,
        enabled: !!userId,
      }),
    profileBadges: (userId: string | undefined) =>
      queryOptions({
        queryKey: keys.profileBadges(userId),
        queryFn: async () =>
          (
            await api.get<UserMemberProfileBadgesDTO>(
              "/customer/user-member-profile/badges",
            )
          ).data,
        enabled: !!userId,
      }),
  },
  infiniteQueries: {
    agenda: (userId: string | undefined) =>
      infiniteQueryOptions({
        queryKey: keys.agenda(userId),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get(`/customer/user-member-booking-agenda`, {
              params: {
                includeBookedCounts: true,
                includeWaitingCounts: true,
                dateFrom:
                  DateTime.now()
                    .set({ millisecond: 0 })
                    .toISO({ suppressMilliseconds: true }) ?? "",
                limit: 10,
                nextPageToken: pageParam,
              },
            })
          ).data;
        },
        ...tokenPagination,
        enabled: !!userId,
      }),
    invoices: (userId: string | undefined) =>
      infiniteQueryOptions({
        queryKey: keys.invoices(userId),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<ApiTokenListResponse<InvoiceDTO>>(
              `/customer/user-member-invoice-list`,
              {
                params: {
                  size: 10,
                  nextPageToken: pageParam,
                },
              },
            )
          ).data;
        },
        ...tokenPagination,
      }),
    currentCreditPack: (userId: string | undefined) =>
      infiniteQueryOptions({
        queryKey: keys.currentCreditPack(userId),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<ApiTokenListResponse<SubscriptionSessionPackDTO>>(
              "/customer/session-pack-subscription/current",
              { params: { nextPageToken: pageParam, limit: 20 } },
            )
          ).data;
        },
        enabled: !!userId,
        ...tokenPagination,
      }),
    previousCreditPack: (userId: string | undefined) =>
      infiniteQueryOptions({
        queryKey: keys.previousCreditPack(userId),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<ApiTokenListResponse<SubscriptionSessionPackDTO>>(
              "/customer/session-pack-subscription/previous",
              { params: { nextPageToken: pageParam, limit: 20 } },
            )
          ).data;
        },
        enabled: !!userId,
        ...tokenPagination,
      }),
    usageHistoryCreditPack: (params: {
      userId: string | undefined;
      subscriptionId: number | undefined;
    }) =>
      infiniteQueryOptions({
        queryKey: keys.usageHistoryCreditPack(params),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<
              ApiTokenListResponse<SubscriptionSessionPackUsageHistoryDTO>
            >(
              `/customer/session-pack-subscription/${params.subscriptionId}/usage-history`,
              { params: { nextPageToken: pageParam, limit: 20 } },
            )
          ).data;
        },
        enabled: !!params.userId && !!params.subscriptionId,
        ...tokenPagination,
      }),
  },
  mutations: {
    payOverdueInvoice: () =>
      mutationOptions({
        mutationFn: async ({
          invoiceNumber,
          paymentMethodId,
        }: {
          userId: string;
          invoiceNumber: string;
          paymentMethodId: string;
        }) => {
          return (
            await api.put<{ invoiceStatus: InvoiceStatus }>(
              `customer/invoice/${invoiceNumber}/pay-past-due/${paymentMethodId}`,
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: keys.id(userId) }),
            queryClient.invalidateQueries({
              queryKey: memberApiAccess.keys.perUser(userId),
            }),
          ]);
        },
      }),
    checkout: () =>
      mutationOptions({
        mutationFn: async (
          params: {
            userId: string;
            idempotencyKey: string;
            paymentMethod?: string;
            promotionCode?: string;
            walletAmount?: number;
          } & (
            | {
                startDate: string;
                membershipId: number;
              }
            | {
                startDate: string;
                sessionPackId: number;
              }
            | {
                products: ProductOrderDetailDTO[];
              }
          ),
        ) => {
          if ("membershipId" in params) {
            return (
              await api.put<SaleResult>(
                `customer/membership-signup/${params.idempotencyKey}`,
                {
                  paymentMethodId: params.paymentMethod,
                  promotionCode: params.promotionCode,
                  membershipId: params.membershipId,
                  startDate: params.startDate,
                  walletAmount: params.walletAmount,
                },
              )
            ).data;
          } else if ("sessionPackId" in params) {
            return (
              await api.put<SaleResult>(
                `customer/session-pack-signup/${params.idempotencyKey}`,
                {
                  paymentMethodId: params.paymentMethod,
                  promotionCode: params.promotionCode,
                  sessionPackId: params.sessionPackId,
                  startDate: params.startDate,
                  walletAmount: params.walletAmount,
                },
              )
            ).data;
          } else {
            return (
              await api.put<SaleResult>(
                `customer/product-order-checkout/${params.idempotencyKey}`,
                {
                  paymentMethodId: params.paymentMethod,
                  promotionCode: params.promotionCode,
                  orderItems: params.products,
                  walletAmount: params.walletAmount,
                },
              )
            ).data;
          }
        },
        onSettled: async (_, __, { userId }) => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: keys.id(userId) }),
            queryClient.invalidateQueries({
              queryKey: memberApiAppointment.keys.perUser(userId),
            }),
            queryClient.invalidateQueries({
              queryKey: memberApiAccess.keys.perUser(userId),
            }),
          ]);
        },
      }),
    deleteAccount: () =>
      mutationOptions({
        mutationFn: async ({}: { userId: string }) => {
          return (await api.post("/customer/user/member/account-delete")).data;
        },
      }),
    membershipCancelByMembershipId: () =>
      mutationOptions({
        mutationFn: async ({
          membershipId,
          patch,
        }: {
          userId: string;
          membershipId: number;
          patch: Partial<{
            cancellationDate: string;
            cancellationReasonId: number;
          }>;
        }) => {
          return (
            await api.post(
              `/customer/user/member/membership/cancel/${membershipId}`,
              patch,
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: keys.id(userId) }),
            queryClient.invalidateQueries({
              queryKey: memberApiAccess.keys.perUser(userId),
            }),
          ]);
        },
      }),
    updateProfile: () =>
      mutationOptions({
        mutationFn: async ({
          patch,
        }: {
          patch: Partial<UserMemberProfileDTO>;
          userId: string;
        }) => {
          return (await api.patch(`/customer/user-member-profile`, patch)).data;
        },
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({ queryKey: keys.id(userId) });
        },
      }),
    updateProfilePicture: () =>
      mutationOptions({
        mutationFn: async ({
          picture: localUri,
        }: {
          picture: string;
          userId: string;
        }) => {
          const formData = new FormData();
          formData.append("file", {
            uri: localUri,
            type: "image/jpeg",
            name: "image.jpg",
          } as any);
          return (
            await api.post(`/customer/user-member-profile/picture`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({ queryKey: keys.id(userId) });
        },
      }),
    resumeMembership: () =>
      mutationOptions({
        mutationFn: async ({
          subscriptionId,
          paymentMethodId,
          earlyPauseEndDate,
        }: {
          subscriptionId: number;
          paymentMethodId?: string;
          userId: string;
          earlyPauseEndDate: string;
        }) => {
          return (
            await api.post<{ invoice: InvoiceDTO }>(
              `/customer/user/member/membership/resume-early/${subscriptionId}`,
              {
                earlyPauseEndDate,
                paymentMethodId,
              },
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: keys.id(userId) }),
            queryClient.invalidateQueries({
              queryKey: memberApiAccess.keys.perUser(userId),
            }),
          ]);
        },
      }),
    changePendingMembershipStartDate: () =>
      mutationOptions({
        mutationFn: async ({
          subscriptionId,
          params,
        }: {
          userId: string;
          subscriptionId: number;
          params:
            | {
                startSubscriptionEffectiveImmediately: true;
              }
            | {
                startSubscriptionEffectiveImmediately: false;
                pendingStartDate: string;
              };
        }) => {
          return (
            await api.put<{ invoice: InvoiceDTO }>(
              `/customer/subscription/${subscriptionId}/start-date`,
              {
                ...params,
              },
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: keys.id(userId) }),
            queryClient.invalidateQueries({
              queryKey: memberApiAccess.keys.perUser(userId),
            }),
          ]);
        },
      }),
    createCheckInPass: () =>
      mutationOptions({
        mutationFn: async ({}: { userId: string | undefined }) => {
          return (await api.put(`/customer/check-in/pass`)).data;
        },
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({ queryKey: keys.id(userId) });
        },
      }),
  },
  links: {
    scaRedirectUrl: (invoiceNumber: string) =>
      `${api.defaults.baseURL}/sca/invoice/${invoiceNumber}`,
    verifyPaymentMethod: (paymentMethodId: string) =>
      `${api.defaults.baseURL}/stripe-verify/${paymentMethodId}`,
  },
};
type SaleResult = {
  invoiceNumber: string;
  invoiceStatus: InvoiceStatus;
};
